/**
 * useGoToRoute Hook
 * Navigation helper for store-front routes
 */

import { useCallback } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { replaceRouteParams, RouteParameters, routes } from '../utils/routes';

export interface GoToRouteOptions {
    replace?: boolean;
    state?: unknown;
}

export interface UseGoToRouteReturn {
    goToRoute: (routeName: routes, params?: RouteParameters, options?: GoToRouteOptions) => void;
    goToPreviousRoute: (fallbackRoute?: routes) => void;
    currentPath: string;
}

export const useGoToRoute = (): UseGoToRouteReturn => {
    const navigate = useNavigate();
    const location = useLocation();

    /**
     * Navigate to a specific route
     */
    const goToRoute = useCallback(
        (routeTemplate: routes, params?: RouteParameters, options?: GoToRouteOptions): void => {
            const routeName = params ? replaceRouteParams(routeTemplate, params) : routeTemplate;

            if (location.pathname !== routeName) {
                navigate(routeName, { replace: options?.replace, state: options?.state });
            }
        },
        [navigate, location.pathname],
    );

    /**
     * Navigate to previous route or fallback
     */
    const goToPreviousRoute = useCallback(
        (fallbackRoute?: routes): void => {
            if (window.history.length > 1) {
                navigate(-1);
            } else if (fallbackRoute) {
                navigate(fallbackRoute);
            }
        },
        [navigate],
    );

    return {
        goToRoute,
        goToPreviousRoute,
        currentPath: location.pathname,
    };
};

export default useGoToRoute;
